import {
    along,
    facesFrontOrBack,
    maxAlong,
    minAlong,
    PlanAxis,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    PairFacts,
    ProductFacts,
    SnapStrategy,
} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {
    DEFAULT_SNAPPING_DISTANCE,
    UPPER_SNAP_MAX_CROSS_AXIS_GAP,
} from 'components/customer/RoomPlanner/helpers/snapping/constants';
import {Box3, Group, Object3DEventMap, Vector3} from 'three';
import {toCandidate} from 'components/customer/RoomPlanner/helpers/snapping/strategies/candidate';

const otherPlanAxis = (axis: PlanAxis): PlanAxis => (axis === 'x' ? 'z' : 'x');

// The world axis an upper's width runs along for the way it faces.
export const widthAxisFor = ({direction}: ProductFacts): PlanAxis =>
    facesFrontOrBack(direction) ? 'x' : 'z';

const setAlong = (vector: Vector3, axis: PlanAxis, value: number) => {
    if (axis === 'x') {
        vector.x = value;
    } else {
        vector.z = value;
    }
};

// Gap between two boxes along one axis; negative when they overlap.
const gapAlong = (a: Box3, b: Box3, axis: PlanAxis) =>
    Math.max(
        minAlong(b, axis) - maxAlong(a, axis),
        minAlong(a, axis) - maxAlong(b, axis)
    );

type BoxSide = 1 | -1;

interface FrontFace {
    axis: PlanAxis;
    side: BoxSide;
}

// Which face of a cabinet's box its doors are on. Rotation 0 backs onto
// wall:back, so its front is the max-z face; the others follow by quarter
// turns.
const frontFaceFor = ({direction}: ProductFacts): FrontFace => {
    switch (direction) {
        case 'right':
            return {axis: 'x', side: 1};
        case 'back':
            return {axis: 'z', side: -1};
        case 'left':
            return {axis: 'x', side: -1};
        default:
            return {axis: 'z', side: 1};
    }
};

const isFrontFace = (front: FrontFace, axis: PlanAxis, side: BoxSide) =>
    front.axis === axis && front.side === side;

// Origins that put the dragged box flush against a face of the other box on
// `axis`, so the two touch without overlapping. A join that would press
// either cabinet's doors against the other is left out: uppers only meet
// back-to-end, end-to-back or side-by-side.
const touchingOrigins = (
    group: Group<Object3DEventMap>,
    pair: PairFacts,
    axis: PlanAxis
) => {
    const {box: selectedBox} = pair.selected;
    const {box: otherBox} = pair.other;
    const selectedFront = frontFaceFor(pair.selected);
    const otherFront = frontFaceFor(pair.other);
    const origin = along(group.position, axis);
    const originToMin = origin - minAlong(selectedBox, axis);
    const originToMax = maxAlong(selectedBox, axis) - origin;
    const origins: number[] = [];

    // Dragged on the other's max side: its min face meets the other's max.
    if (
        !isFrontFace(selectedFront, axis, -1) &&
        !isFrontFace(otherFront, axis, 1)
    ) {
        origins.push(maxAlong(otherBox, axis) + originToMin);
    }
    // Dragged on the other's min side: its max face meets the other's min.
    if (
        !isFrontFace(selectedFront, axis, 1) &&
        !isFrontFace(otherFront, axis, -1)
    ) {
        origins.push(minAlong(otherBox, axis) - originToMax);
    }

    return origins;
};

// Origins that line the dragged box's faces up with the other box's faces on
// `axis`, so both share a plane on that side.
const alignedOrigins = (
    group: Group<Object3DEventMap>,
    pair: PairFacts,
    axis: PlanAxis
) => {
    const {box: selectedBox} = pair.selected;
    const {box: otherBox} = pair.other;
    const origin = along(group.position, axis);
    const originToMin = origin - minAlong(selectedBox, axis);
    const originToMax = maxAlong(selectedBox, axis) - origin;

    return [
        minAlong(otherBox, axis) + originToMin,
        maxAlong(otherBox, axis) - originToMax,
    ];
};

// Snaps an upper onto another upper without covering either one's doors.
// Facing the same way it goes beside the other on the left or right. At
// right angles its back goes against the other's end, or its end against
// the other's back, and when close enough the free edge is lined up with the
// other's back or front so the two meet as a corner. Tops are kept level.
const findUpperSideBySideSnap = (
    cabinetGroup: Group<Object3DEventMap>,
    pair: PairFacts,
    targetPos: Vector3
): Vector3 | null => {
    const {box: selectedBox} = pair.selected;
    const {box: otherBox} = pair.other;
    const widthAxis = widthAxisFor(pair.selected);
    const depthAxis = otherPlanAxis(widthAxis);
    const isPerpendicular = widthAxisFor(pair.other) !== widthAxis;

    const snappedY =
        cabinetGroup.position.y + (otherBox.max.y - selectedBox.max.y);

    const propose = (moves: Array<[PlanAxis, number]>) => {
        const proposed = targetPos.clone();
        moves.forEach(([axis, value]) => setAlong(proposed, axis, value));
        proposed.y = snappedY;

        return proposed;
    };

    // The nearest proposal in range. Ties go to the later proposal, as they
    // did when this was written (CO-5777).
    const nearest = (proposals: Vector3[]): Vector3 | null => {
        let best: Vector3 | null = null;
        let bestDistance = Infinity;
        proposals.forEach((proposed) => {
            const distance = proposed.distanceTo(targetPos);
            if (
                distance <= DEFAULT_SNAPPING_DISTANCE &&
                distance <= bestDistance
            ) {
                bestDistance = distance;
                best = proposed;
            }
        });

        return best;
    };

    if (isPerpendicular) {
        // Either the dragged upper's back meets the other's end (contact
        // along the dragged depth), or the dragged upper's end meets the
        // other's back (contact along the dragged width). In both, the free
        // axis lines the two up so they meet as a corner.
        const axes: PlanAxis[] = [depthAxis, widthAxis];

        const cornered = nearest(
            axes.flatMap((contactAxis) => {
                const alignAxis = otherPlanAxis(contactAxis);
                const alignments = alignedOrigins(
                    cabinetGroup,
                    pair,
                    alignAxis
                );

                return touchingOrigins(cabinetGroup, pair, contactAxis).flatMap(
                    (contact) =>
                        alignments.map((aligned) =>
                            propose([
                                [contactAxis, contact],
                                [alignAxis, aligned],
                            ])
                        )
                );
            })
        );
        if (cornered) return cornered;

        return nearest(
            axes
                .filter(
                    (contactAxis) =>
                        gapAlong(
                            selectedBox,
                            otherBox,
                            otherPlanAxis(contactAxis)
                        ) <= UPPER_SNAP_MAX_CROSS_AXIS_GAP
                )
                .flatMap((contactAxis) =>
                    touchingOrigins(cabinetGroup, pair, contactAxis).map(
                        (contact) => propose([[contactAxis, contact]])
                    )
                )
        );
    }

    // Facing the same way, the only join that keeps both fronts clear is
    // side by side, so the contact is along the width.
    if (
        gapAlong(selectedBox, otherBox, depthAxis) >
        UPPER_SNAP_MAX_CROSS_AXIS_GAP
    ) {
        return null;
    }

    return nearest(
        touchingOrigins(cabinetGroup, pair, widthAxis).map((origin) =>
            propose([[widthAxis, origin]])
        )
    );
};

// Two uppers facing the same way, side by side, or at right angles, meeting
// as a corner.
export const getUpperSideBySideSnap: SnapStrategy = (
    cabinetGroup,
    otherProduct,
    ctx,
    pair
) =>
    toCandidate(
        findUpperSideBySideSnap(cabinetGroup, pair, ctx.targetPos),
        ctx.targetPos
    );
